import { Fragment, useState, type ReactNode } from 'react';
import { ScrollView, type NativeScrollEvent, type NativeSyntheticEvent } from 'react-native';
import { XStack, YStack } from 'tamagui';
import { useThemeColors } from '../../hooks/useThemeColors';
import { colors } from '../../theme/colors';

/**
 * Shared horizontal card carousel — a snapping, swipeable strip with dot
 * indicators, used where a row of cards can't fit a phone width (the landing
 * plans teaser and the /pricing cards). Cards keep their own width via
 * `renderCard(item, cardWidth)`; the carousel owns the snap math, the peek
 * affordance, and the active-dot tracking.
 *
 * Pixel-math sizing reads `viewportWidth` from the caller's
 * `useWindowDimensions()`; the row-vs-carousel decision itself stays on
 * `useBreakpoint()` at the call site (ADR-0078).
 */

/** The resting card sits `EDGE` from the leading screen edge; `PEEK` of the
 *  next card stays visible as a swipe affordance. Card width is what's left. */
const CARD_GAP = 14;
const CAROUSEL_EDGE = 20;
const CAROUSEL_PEEK = 36;

/** One card in the strip: a stable key plus a render thunk that lays the card
 *  out at the carousel's resolved width. Callers map their own items to this
 *  shape, which keeps CardCarousel non-generic — a generic `<T>` here pushed
 *  tsc past its recursion limit through the render-prop inference. */
export interface CarouselCard {
  key: string;
  render: (cardWidth: number) => ReactNode;
}

export function CardCarousel({
  cards,
  viewportWidth,
  gap = 16,
  marginTop,
}: {
  cards: ReadonlyArray<CarouselCard>;
  viewportWidth: number;
  /** Vertical gap between the scroller and the dot row. */
  gap?: number;
  /** Top margin on the whole carousel block (omit to rely on parent spacing). */
  marginTop?: number;
}) {
  const theme = useThemeColors();
  const [activeIndex, setActiveIndex] = useState(0);
  const cardWidth = viewportWidth - CAROUSEL_EDGE - CAROUSEL_PEEK;
  const snapInterval = cardWidth + CARD_GAP;

  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const raw = Math.round(event.nativeEvent.contentOffset.x / snapInterval);
    const next = Math.min(Math.max(raw, 0), cards.length - 1);
    setActiveIndex((prev) => (prev === next ? prev : next));
  };

  return (
    <YStack gap={gap} {...(marginTop != null ? { marginTop } : {})}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        decelerationRate="fast"
        snapToInterval={snapInterval}
        snapToAlignment="start"
        disableIntervalMomentum
        onScroll={handleScroll}
        scrollEventThrottle={16}
        contentContainerStyle={{
          paddingLeft: CAROUSEL_EDGE,
          paddingRight: CAROUSEL_PEEK,
          gap: CARD_GAP,
        }}
      >
        {cards.map((card) => (
          <Fragment key={card.key}>{card.render(cardWidth)}</Fragment>
        ))}
      </ScrollView>

      <XStack alignSelf="center" gap={6} alignItems="center">
        {cards.map((card, index) => (
          <YStack
            key={card.key}
            transition="quick"
            height={6}
            width={index === activeIndex ? 22 : 6}
            borderRadius={999}
            backgroundColor={index === activeIndex ? colors.accentCerulean : theme.borderMuted}
          />
        ))}
      </XStack>
    </YStack>
  );
}
